import React, { useEffect } from 'react'
import { useTranslation } from '../../../shared/hooks/useIntractionTranslate'
import { Box, Typography } from '@mui/material'
import { ChevronLeftIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { INTERAX_COLORS, INTERAX_TYPOGRAPHY } from '../../../constants/colors'
import {
  useQuestionnaire,
  useQuestionnaireAnswer,
  useSetQuestionnaire,
  useUpdateQuestionnaireAnswer,
  useUpdateUser
} from '../../../apiLayer'
import { useUser } from '../../../hooks/useUser'
import { useQueryClient } from '@tanstack/react-query'
import { QUERY_KEYS } from '../../../apiLayer/queryKeys'
import toast from 'react-hot-toast'
import { OnboardingChapter } from '../../../../../pure-js/enums/OnboardingChapter'
import { DefaultModal } from '../../../shared/components/DefaultModal'
import {
  AssessmentModalButtonWrapper,
  AssessmentModalProgressSlider,
  AssessmentModalRatingScale
} from './AssessmentModal/index'
import { ASSESSMENT_TOTAL_QUESTIONS } from '../../shared/utils/onboardingProgress'

interface AssessmentModalProps {
  isOpen: boolean
  onClose: () => void
  onBack?: () => void
  onComplete: (answers: Record<number, string>) => void
  className?: string
}

/**
 * AssessmentModal — 37-question Likert assessment.
 * Auto-advances to next question 400ms after a rating is selected.
 */
export const AssessmentModal: React.FC<AssessmentModalProps> = ({
  isOpen,
  onClose,
  onBack,
  onComplete,
  className = ''
}) => {
  const { t } = useTranslation()
  const { data: user } = useUser()
  const queryClient = useQueryClient()
  const [currentQuestionIndex, setCurrentQuestionIndex] = React.useState(1)
  const [answers, setAnswers] = React.useState<Record<number, string>>({})
  const [isSaving, setIsSaving] = React.useState(false)

  const userId = user?.id || ''
  const context = 'preassessment'

  const { data: questionnaireResponse } = useQuestionnaire({ userId, context })
  const questionnaire = questionnaireResponse?.data

  const { data: currentAnswerResponse } = useQuestionnaireAnswer({
    questionnaireId: questionnaire?.id || '',
    questionId: currentQuestionIndex.toString()
  })
  const currentAnswer = currentAnswerResponse?.data

  const setQuestionnaireMutation = useSetQuestionnaire()
  const updateAnswerMutation = useUpdateQuestionnaireAnswer()
  const updateUserMutation = useUpdateUser()

  const totalQuestions = ASSESSMENT_TOTAL_QUESTIONS

  useEffect(() => {
    if (!isOpen || !userId) return
    const initQuestionnaire = async () => {
      try {
        await setQuestionnaireMutation.mutateAsync({ userId, context })
      } catch (_error) {
        // continue
      }
    }
    initQuestionnaire()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, userId])

  useEffect(() => {
    if (!isOpen) return
    if (user?.currentAssessmentQuestion && user.currentAssessmentQuestion > 0) {
      setCurrentQuestionIndex(user.currentAssessmentQuestion)
    } else {
      setCurrentQuestionIndex(1)
    }
  }, [isOpen, user?.currentAssessmentQuestion])

  useEffect(() => {
    if (!currentAnswer?.answer) return
    setAnswers((previous) => {
      const existing = previous[currentQuestionIndex]
      if (existing === currentAnswer.answer) return previous
      return { ...previous, [currentQuestionIndex]: currentAnswer.answer }
    })
  }, [currentAnswer?.answer, currentQuestionIndex])

  const handleAnswerSelect = (questionId: number, answer: string) => {
    setAnswers((prev) => ({ ...prev, [questionId]: answer }))
  }

  const handlePrevious = () => {
    if (currentQuestionIndex > 1) setCurrentQuestionIndex(currentQuestionIndex - 1)
  }

  const saveAndAdvance = async (overrideAnswer?: string) => {
    const currentAnswerValue = overrideAnswer ?? answers[currentQuestionIndex] ?? currentAnswer?.answer
    if (!currentAnswerValue || !questionnaire) return

    setIsSaving(true)
    try {
      await updateAnswerMutation.mutateAsync({
        questionnaire,
        questionId: currentQuestionIndex.toString(),
        textKeyName: `gamer.assessmentModal.questions.${currentQuestionIndex}`,
        userId,
        answer: currentAnswerValue.toString()
      })

      if (currentQuestionIndex < totalQuestions) {
        const nextQuestion = currentQuestionIndex + 1
        await updateUserMutation.mutateAsync({
          id: userId,
          userData: { currentAssessmentQuestion: nextQuestion }
        })
        setCurrentQuestionIndex(nextQuestion)
      } else {
        const completedSteps = user?.completedOnboardingSteps || []
        const shouldAddAssessment =
          completedSteps.includes(OnboardingChapter.Demographics) &&
          !completedSteps.includes(OnboardingChapter.Assessment)
        const updatedSteps = shouldAddAssessment ? [...completedSteps, OnboardingChapter.Assessment] : completedSteps

        await updateUserMutation.mutateAsync({
          id: userId,
          userData: {
            currentAssessmentQuestion: undefined,
            ...(shouldAddAssessment && { completedOnboardingSteps: updatedSteps })
          }
        })

        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CURRENT_USER] })
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.USER_DETAIL, userId] })
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.QUESTIONNAIRE] })
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.QUESTIONNAIRE_ANSWER] })

        onComplete(answers)
      }
    } catch (_error) {
      toast.error('Failed to save answer. Please try again.')
    } finally {
      setIsSaving(false)
    }
  }

  const handleNext = () => saveAndAdvance()
  const handleAutoAdvance = (rating: string) => saveAndAdvance(rating)

  const currentAnswerValue = answers[currentQuestionIndex] ?? currentAnswer?.answer ?? ''
  const selectedRating = currentAnswerValue === '' ? undefined : currentAnswerValue
  const isLastQuestion = currentQuestionIndex === totalQuestions
  const hasAnswer = Boolean(selectedRating)

  const useEightPointScale = currentQuestionIndex >= 26
  const ratingOptions = useEightPointScale ? [1, 2, 3, 4, 5, 6, 7, 8] : [1, 2, 3, 4, 5, 6, 7]

  const currentQuestionTextKey = `gamer.assessmentModal.questions.${currentQuestionIndex}`

  if (!isOpen) return null

  return (
    <DefaultModal
      isOpen={isOpen}
      onClose={onClose}
      className={className}
      showCloseButton={false}
      maxWidth="800px"
      backgroundColor={INTERAX_COLORS.offBlack}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Header: back icon left · step indicator center · X right */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
          <Box
            onClick={onBack ?? onClose}
            sx={{
              width: 36,
              height: 36,
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: INTERAX_COLORS.thunderGrey,
              border: `1px solid rgba(255,255,255,0.1)`,
              transition: 'all 0.15s',
              '&:hover': { color: INTERAX_COLORS.white, borderColor: INTERAX_COLORS.thunderGrey }
            }}
          >
            <ChevronLeftIcon style={{ width: 18, height: 18 }} />
          </Box>

          {/* Step 2 of 2 indicator — matches demographics header */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75, flex: 1 }}>
            <Typography
              sx={{
                ...INTERAX_TYPOGRAPHY.body3,
                color: INTERAX_COLORS.thunderGrey,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                fontWeight: 600,
                fontSize: '11px'
              }}
            >
              {t('gamer.assessmentModal.stepLabel')}
            </Typography>
            <Box sx={{ width: 160, height: 3, backgroundColor: INTERAX_COLORS.thunderGrey20, borderRadius: 2 }}>
              <Box sx={{ width: '100%', height: '100%', backgroundColor: INTERAX_COLORS.sun, borderRadius: 2 }} />
            </Box>
          </Box>

          <Box
            onClick={onClose}
            sx={{
              width: 36,
              height: 36,
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: INTERAX_COLORS.thunderGrey,
              border: `1px solid rgba(255,255,255,0.1)`,
              transition: 'all 0.15s',
              '&:hover': { color: INTERAX_COLORS.white, borderColor: INTERAX_COLORS.thunderGrey }
            }}
          >
            <XMarkIcon style={{ width: 18, height: 18 }} />
          </Box>
        </Box>

        {!questionnaire ? (
          <Box
            sx={{
              backgroundColor: INTERAX_COLORS.heritageBlue40,
              borderRadius: '16px',
              p: 4,
              textAlign: 'center'
            }}
          >
            <Typography sx={{ color: INTERAX_COLORS.white }}>{t('gamer.assessmentModal.loading')}</Typography>
          </Box>
        ) : (
          <Box
            sx={{
              borderRadius: '16px',
              p: 4,
              display: 'flex',
              flexDirection: 'column',
              gap: 4
            }}
          >
            <AssessmentModalProgressSlider
              currentQuestionIndex={currentQuestionIndex}
              totalQuestions={totalQuestions}
            />

            <Box
              sx={{
                backgroundColor: INTERAX_COLORS.heritageBlue40,
                borderRadius: '12px',
                p: { xs: 3, sm: 3, md: 4 },
                display: 'flex',
                flexDirection: 'column',
                gap: 4
              }}
            >
              <Typography
                sx={{
                  ...INTERAX_TYPOGRAPHY.headline3,
                  color: INTERAX_COLORS.white,
                  fontWeight: 700,
                  textAlign: 'center',
                  minHeight: '96px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                {t(currentQuestionTextKey)}
              </Typography>

              <AssessmentModalRatingScale
                ratingOptions={ratingOptions}
                selectedRating={selectedRating}
                onSelect={(ratingValue: string) => handleAnswerSelect(currentQuestionIndex, ratingValue)}
                onAutoAdvance={!isSaving ? handleAutoAdvance : undefined}
              />
            </Box>

            <AssessmentModalButtonWrapper
              onPrevious={handlePrevious}
              onNext={handleNext}
              previousLabel={t('gamer.assessmentModal.previous')}
              nextLabel={t('gamer.assessmentModal.next')}
              continueLabel={t('gamer.assessmentModal.continue')}
              savingLabel="Saving..."
              isPreviousDisabled={currentQuestionIndex === 1}
              isNextDisabled={!hasAnswer || isSaving}
              isLastQuestion={isLastQuestion}
              isSaving={isSaving}
            />
          </Box>
        )}
      </Box>
    </DefaultModal>
  )
}
